import { Box, Button, Card, Checkbox, Chip, Grid, IconButton, Tooltip } from "@mui/material";
import { CaseDisplayGroup, CaseDisplayGroupItem, CaseDisplayGroupType } from "generated/graphql";
import { Fragment, useEffect, useState } from "react";
import theme, { EllipsisSX } from "theme";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Navigate, Outlet, useNavigate, useParams } from "react-router";
import { useAppSelector } from "redux/hooks";
import { selectCaseDisplayGroups } from "redux/slices/lensEditor";
import { P } from "theme/typography";
import CopyToClipboard from "components/CopyToClipboard";
import { ClipboardDisplayGroup, makeClipboardDisplayGroupsForCopy } from "./ImportUtils";
import { Add, Edit, UnfoldLess, UnfoldMore } from "@mui/icons-material";
import NewCaseDisplayGroupDialog from "./NewCaseDisplayGroupDialog";
import EditCaseDisplayGroupDialog from "./EditCaseDisplayGroupDialog";
import { useCaseCalc, useCaseProperty } from "./useCaseProperty";
import { CASE_DISPLAY_GROUP_DEFAULT_NAME } from "./case-display-groups.loader";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { ImportCaseDisplayGroupsDialog } from "./ImportCaseDisplayGroupsDialog";
import { createCaseDisplayGroupItemComparator } from "./util";

function CaseDisplayGroupListItemProperty({ item, onClick }: { item: CaseDisplayGroupItem; onClick: () => void }) {
  const caseProperty = useCaseProperty(item.casePropertyKey);
  const caseCalc = useCaseCalc(item.caseCalcKey);

  return caseProperty !== undefined ? (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          ...EllipsisSX,
        }}>
        <Tooltip title={`Key: ${caseProperty.key}`} placement="bottom">
          <P
            onClick={onClick}
            variant="sm"
            sx={{
              ...EllipsisSX,
              cursor: "pointer",
              ":hover": {
                textDecoration: "underline",
              },
              color: caseProperty.isHidden ? theme.palette.grey[300] : undefined,
            }}>
            {caseProperty.displayName}
          </P>
        </Tooltip>
        <DescriptionTooltip description={caseProperty.description} />
      </Box>
      <Chip color="primary" label={caseProperty.dataType} size="small" />
    </Box>
  ) : caseCalc !== undefined ? (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
        }}>
        <Tooltip title={`Key: ${caseCalc.key}`} placement="bottom">
          <P
            onClick={onClick}
            variant="sm"
            sx={{
              ...EllipsisSX,
              cursor: "pointer",
              ":hover": {
                textDecoration: "underline",
              },
            }}>
            {caseCalc.displayName}
          </P>
        </Tooltip>
        <DescriptionTooltip description={caseCalc.description} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip color="secondary" label="Calc" size="small" />
        <Chip color="primary" label={caseCalc.dataType} size="small" />
      </Box>
    </Box>
  ) : (
    <Fragment key={item.id} />
  );
}

const CaseDisplayGroupListItem = ({
  cdg,
  onClick,
  onEdit,
  isActive,
  showProperties,
  checked,
  toggleChecked,
}: {
  cdg: CaseDisplayGroup;
  onEdit?: () => void;
  onClick: () => void;
  isActive: boolean;
  showProperties: boolean;
  checked: boolean;
  toggleChecked: (name: string) => void;
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          borderRadius: 1,
          pl: 1,
          py: 1,
          backgroundColor: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}>
          <P
            onClick={onClick}
            variant="lgBold"
            sx={{
              ...theme.mixins.linkLike,
              color: "inherit",
              textDecoration: isActive ? "underline" : "none",
            }}>
            {cdg.name}
          </P>
          <DescriptionTooltip description={cdg.description} />
          {cdg.type === CaseDisplayGroupType.Table && <Chip label="Table" size="small" />}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}>
          {onEdit !== undefined && (
            <Tooltip title="Edit Group">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit();
                }}>
                <Edit />
              </IconButton>
            </Tooltip>
          )}
          <Checkbox checked={checked} onChange={() => toggleChecked(cdg.name)} size="small" color="secondary" />
        </Box>
      </Box>
      {showProperties && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 2 }}>
          {cdg.items.toSorted(createCaseDisplayGroupItemComparator(cdg.type)).map((item) => (
            <CaseDisplayGroupListItemProperty item={item} key={item.id} onClick={onClick} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default function DisplayGroups() {
  const { lensId } = useParamNumbers(["lensId"]);
  const { caseDisplayGroupName } = useParams();

  if (lensId === undefined) {
    return <Navigate to=".." />;
  }

  const [newCaseDisplayGroup, setNewCaseDisplayGroup] = useState(false);
  const [editCaseDisplayGroup, setEditCaseDisplayGroup] = useState<CaseDisplayGroup | undefined>(undefined);
  const caseDisplayGroups = useAppSelector(selectCaseDisplayGroups);
  const defaultCaseDisplayGroup = caseDisplayGroups.find((cdg) => cdg.name === CASE_DISPLAY_GROUP_DEFAULT_NAME);
  const [displayCaseDisplayGroups, setDisplayCaseDisplayGroups] = useState<CaseDisplayGroup[]>([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const navigate = useNavigate();
  const [selectedGroups, setSelectedGroups] = useState<CaseDisplayGroup[]>([]);
  const createGroupsForCopy: ClipboardDisplayGroup[] = selectedGroups.map(makeClipboardDisplayGroupsForCopy);
  const [showProperties, setShowProperties] = useState(true);

  const toggleGroupChecked = (groupName: string): void => {
    setSelectedGroups((currGroups) => {
      const foundIndex = currGroups.findIndex((cg) => cg.name === groupName);
      const group = caseDisplayGroups.find((cdg) => cdg.name === groupName);

      if (foundIndex > -1) {
        currGroups.splice(foundIndex);
        return currGroups.slice();
      } else if (group !== undefined) {
        return [...currGroups, group];
      } else {
        return currGroups;
      }
    });
  };

  useEffect(() => {
    setDisplayCaseDisplayGroups(
      caseDisplayGroups
        .filter((cdg) => cdg.name !== CASE_DISPLAY_GROUP_DEFAULT_NAME)
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [caseDisplayGroups]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 2, gap: 2 }}>
        <Button variant="outlined" onClick={() => setShowImportDialog(true)}>
          Import
        </Button>
      </Box>

      {newCaseDisplayGroup && (
        <NewCaseDisplayGroupDialog
          open={newCaseDisplayGroup}
          close={(newGroup) => {
            setNewCaseDisplayGroup(false);
            if (newGroup !== undefined) {
              navigate(encodeURIComponent(newGroup.name));
            }
          }}
          lensId={lensId}
        />
      )}
      {editCaseDisplayGroup !== undefined && (
        <EditCaseDisplayGroupDialog
          open={editCaseDisplayGroup !== undefined}
          close={() => setEditCaseDisplayGroup(undefined)}
          caseDisplayGroup={editCaseDisplayGroup}
          lensId={lensId}
        />
      )}
      <Grid
        container
        spacing={2}
        sx={{
          // fill the remaining space but don't grow
          flex: 1,
          minHeight: 0,
          height: "100%",
        }}>
        <Grid item xs={4} md={4} sx={{ height: "100%" }}>
          <Card sx={{ p: 1, minHeight: 0, display: "flex", flexDirection: "column", maxHeight: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
              <Tooltip title="Create Group">
                <IconButton onClick={() => setNewCaseDisplayGroup(true)}>
                  <Add />
                </IconButton>
              </Tooltip>
              <div>
                <Tooltip title="Toggle properties">
                  <IconButton onClick={() => setShowProperties((prev) => !prev)}>
                    {showProperties ? <UnfoldLess /> : <UnfoldMore />}
                  </IconButton>
                </Tooltip>
                <CopyToClipboard
                  text={() => JSON.stringify(createGroupsForCopy, null, 2)}
                  title="Copy Selected Groups for Import"
                />
              </div>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {defaultCaseDisplayGroup !== undefined && (
                <CaseDisplayGroupListItem
                  cdg={defaultCaseDisplayGroup}
                  onClick={() => navigate(defaultCaseDisplayGroup.name)}
                  isActive={caseDisplayGroupName === defaultCaseDisplayGroup.name}
                  showProperties={showProperties}
                  checked={!!selectedGroups.find((g) => g.name === defaultCaseDisplayGroup.name)}
                  toggleChecked={toggleGroupChecked}
                />
              )}
              {displayCaseDisplayGroups.map((cdg) => (
                <CaseDisplayGroupListItem
                  key={cdg.id}
                  cdg={cdg}
                  onClick={() => navigate(encodeURIComponent(cdg.name))}
                  onEdit={() => setEditCaseDisplayGroup(cdg)}
                  isActive={caseDisplayGroupName === cdg.name}
                  showProperties={showProperties}
                  checked={!!selectedGroups.find((g) => g.name === cdg.name)}
                  toggleChecked={toggleGroupChecked}
                />
              ))}
            </Box>
          </Card>
        </Grid>
        <Grid item xs={8} md={8} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Outlet />
        </Grid>
      </Grid>
      {showImportDialog && <ImportCaseDisplayGroupsDialog onClose={() => setShowImportDialog(false)} lensId={lensId} />}
    </Box>
  );
}
